import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";

export default function SellerApplicationModal() {
  const { sellerAppOpen, closeSellerApp } = useUI();
  const [form, setForm] = useState({
    name: "",
    email: "",
    social_handle: "",
    destinations: "",
    motivation: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!sellerAppOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.destinations || !form.motivation) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/seller-applications", form);
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (success) {
      setForm({ name: "", email: "", social_handle: "", destinations: "", motivation: "" });
      setSuccess(false);
    }
    closeSellerApp();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 bg-black/65 backdrop-blur-sm overflow-y-auto"
      onClick={handleClose}
      data-testid="seller-app-modal"
    >
      <div
        className="bg-[var(--paper)] border-[1.5px] border-[var(--line)] rounded-[24px] max-w-[560px] w-full my-8 p-8 sm:p-10 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="modal-close"
          className="absolute top-4 right-5 text-[var(--ink2)] hover:text-[var(--red)] text-2xl leading-none w-8 h-8 grid place-items-center transition-colors"
          aria-label="Close"
        >×</button>

        {!success ? (
          <>
            <span className="eyebrow">◆ Invite-only · Vetted creators</span>
            <h2 className="h-display text-[36px] sm:text-[42px] mt-3 leading-[1.05]">
              Sell your itineraries on Nirno
            </h2>
            <p className="mt-3 text-[14px] leading-[1.65] text-[var(--ink2)]">
              We handpick every creator on the platform. Tell us about yourself and we'll be in touch.
            </p>

            <form onSubmit={handleSubmit} className="mt-7 space-y-3">
              <div className="inp-wrap">
                <label className="lbl">Full name</label>
                <input className="inp" value={form.name} onChange={set("name")} data-testid="app-name" placeholder="Aiko Kawakami" />
              </div>
              <div className="inp-wrap">
                <label className="lbl">Email address</label>
                <input className="inp" type="email" value={form.email} onChange={set("email")} data-testid="app-email" placeholder="you@example.com" />
              </div>
              <div className="inp-wrap">
                <label className="lbl">Instagram / YouTube / TikTok handle</label>
                <input className="inp" value={form.social_handle} onChange={set("social_handle")} data-testid="app-social" placeholder="@yourhandle" />
              </div>
              <div className="inp-wrap">
                <label className="lbl">Where have you travelled?</label>
                <textarea
                  className="inp min-h-[96px] resize-y"
                  value={form.destinations}
                  onChange={set("destinations")}
                  data-testid="app-destinations"
                  placeholder="Japan, Patagonia, Morocco, Nepal…"
                />
              </div>
              <div className="inp-wrap">
                <label className="lbl">Why do you want to sell on Nirno?</label>
                <textarea
                  className="inp min-h-[120px] resize-y"
                  value={form.motivation}
                  onChange={set("motivation")}
                  data-testid="app-motivation"
                  placeholder="Tell us what makes your routes worth paying for."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="app-submit"
                className="btn btn-solid btn-lg full mt-2"
              >
                {submitting ? "Sending…" : "Send application →"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="app-success" className="py-6">
            <div className="w-16 h-16 rounded-full bg-[var(--red)] text-white grid place-items-center text-3xl mx-auto">
              ✓
            </div>
            <span className="eyebrow block text-center mt-6">◆ Submitted</span>
            <h3 className="h-display text-[32px] mt-2 text-center">Application received.</h3>
            <p className="mt-3 text-[14px] text-[var(--ink2)] text-center max-w-[360px] mx-auto leading-[1.65]">
              We'll be in touch within 3–5 days. Keep travelling.
            </p>
            <button
              onClick={handleClose}
              data-testid="app-close-success"
              className="btn btn-ghost btn-lg full mt-7"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
